{
    "introduction": {
        "title": "Hi, I'm Cephable.",
        "back": "Back to introduction",
        "description": "I'm your personal AI to help you control, create, and automate using the tools you already love — across all your connections. There are a few things you should know about me.",
        "action": "Let's get started!",
        "recording": {
            "title": "Your voice and video stay local.",
            "description": "I never record, stream, or send your audio or video to the cloud. All inputs are processed on-device — privately and securely."
        },
        "data": {
            "title": "Your data is yours.",
            "description": "I don't use your commands or activity to train models for others. Everything stays private — and your app controls move with you across connections."
        },
        "offline": {
            "title": "I work when other tools don't.",
            "description": "Intelligent Tasks like generating content, rewriting, translating, and dictation all run locally. No cloud required. That means I work even when you're offline — and your data doesn't leave your device."
        },
        "empower": {
            "title": "Built to empower, not exploit.",
            "description": "I am designed to enhance your control — not monitor it. No invasive tracking. No creepy data collection. Just input that works for you."
        }
    },
    "my-capabilities": {
        "title": "Perfect! Here's how I can help.",
        "back": "Back to capabilities",
        "actionTitle": "See Me In Action",

        "description": "Based on your interests, here are the features and capabilities most relevant to your workflow.",
        "action": {
            "title": "See Me In Action",
            "description": "Explore some examples of how I can assist you in your daily tasks."
        }
    },
    "see-me": {
        "title": "Want to see how I've helped others like you?",
        "back": "Back to use cases",
        "description": "Check out some real-world examples of how I've transformed workflows across different industries and roles."
    },
    "complete": {
        "title": "Ready to Start Using Me?",
        "description": "You've seen what I can do! Now let's get you set up and start transforming your workflow. I'll be here to help you every step of the way.",
        "action": "Complete Setup & Start Using Cephable"
    },
    "apps": {
        "title": "I Support the Tools You Already Love",
        "description": "With pre-configured support for 200+ applications — I work with the software you use every day. No setup required.",
        "note": "Don't see an app you use? No problem — I can generate controls or help you create custom workflows that work for you.",
        "andMore": "And More"
    },
    "actions": {
        "ctaTry": "Try it now",
        "ctaWatch": "Watch demo",
        "apps": "I am Compatible with:",
        "abilities": "My abilities used:"
    },
    "interests": {
        "title": "What can I help you with?",
        "back": "Back to interests",
        "description": "I can control apps on your desktop and connected devices. Let's personalize your experience - what areas interest you most? ",
        "note": "Select all that apply and I'll show you the most relevant features and examples for your workflow.",
        "action": "Continue with {{count}} interests",
        "empty": "Select at least one interest.",
        "filter": "Filter by Interests Type",
        "create_design": {
            "label": "Create & Design",
            "shortLabel": "Design",
            "description": "For artists, marketers, and content creators."
        },
        "analyze_report": {
            "label": "Analyze & Report",
            "shortLabel": "Analyze",
            "description": "For analysts, researchers, and business professionals."
        },
        "communicate_collaborate": {
            "label": "Communicate & Collaborate",
            "shortLabel": "Communicate",
            "description": "For sellers, meetings, and remote teams."
        },
        "build_develop": {
            "label": "Build & Develop",
            "shortLabel": "Develop",
            "description": "For developers, engineers, and technical professionals."
        },
        "game_play": {
            "label": "Game & Play",
            "shortLabel": "Gaming",
            "description": "For gamers, streamers, and media controls."
        },
        "learn_teach": {
            "label": "Learn & Teach",
            "shortLabel": "Learning",
            "description": "For educators, students, and instruction."
        },
        "manage_organize": {
            "label": "Manage & Organize",
            "shortLabel": "Organizing",
            "description": "For project managers, coordinators, and organizers."
        },
        "adapt_access": {
            "label": "Adapt & Access",
            "shortLabel": "Accessibility",
            "description": "For adaptive controls and personalized accessibility needs."
        },
        "everything": {
            "label": "A little bit of everything",
            "shortLabel": "Everything",
            "description": "For general computer users and multi-taskers."
        }
    },
    "capabilities": {
        "title": "My Core AI Capabilities",
        "description": "I combine advanced AI with powerful input methods to give you seamless control over your apps and devices. Here are some of the things I can do.",
        "voice_controls": {
            "title": "Voice Controls",
            "description": "I understand natural language commands so you can control any app with your voice.",
            "longDescription": "I let you speak naturally and still get things done. Whether you want to open apps, navigate menus, or trigger workflows, I translate plain English into hands-free and reliable actions."
        },
        "smart_dictation": {
            "title": "Intelligent Dictation",
            "description": "I convert your speech to text with context awareness and formatting.",
            "longDescription": "I listen carefully and capture your words accurately. I add punctuation, fix capitalization, and adapt to your style so your writing looks polished without extra effort."
        },
        "ai_text_generation": {
            "title": "AI Text Generation",
            "description": "I can create emails, documents, code, and creative content on the fly.",
            "longDescription": "I help you draft, summarize, or expand on anything directly in your apps. From emails and notes to code snippets and documents, I make sure you never start from a blank page."
        },
        "head_face_controls": {
            "title": "Head & Face Controls",
            "description": "I let you trigger actions with your head and facial movements.",
            "longDescription": "I translate your natural head movements and expressions into precise input. You can send an email with a smile, turn a page with a nod, or click with a raise of the eyebrows."
        },
        "natural_language_voice_clicking": {
            "title": "Natural Language Voice Clicking",
            "description": "I let you say what you see to click buttons, links, and menus on screen.",
            "longDescription": "When you speak the name of something on your screen, I find it and interact with it for you. No memorized commands — just describe what you want and I’ll handle the click."
        },
        "keyboard_mouse_emulation": {
            "title": "Keyboard & Mouse Emulation",
            "description": "I emulate keys, clicks, and scrolling so you can control apps without hardware.",
            "longDescription": "I send keystrokes, mouse movements, clicks, and scroll events anywhere you need them. Combine me with voice or head controls to fully replace or enhance traditional input devices."
        },
        "profiles_personalization": {
            "title": "Personalization",
            "description": "I remember your preferences and adapt with saved app controls.",
            "longDescription": "I keep track of your favorite apps, commands, and preferences. Switch between app controls automatically or manually, and I’ll always show the right controls for what you’re doing."
        },
        "cross_device_connectivity": {
            "title": "Cross-Device Connectivity",
            "description": "I let you use your phone to control your computer.",
            "longDescription": "I connect your mobile device to your desktop so you can launch commands, dictate, or navigate with ease. Perfect for presentations, remote sessions, or casual use."
        },
        "quick_action_buttons": {
            "title": "Quick Action Buttons",
            "description": "I give you app-aware buttons for fast actions.",
            "longDescription": "I surface context-sensitive buttons that adapt to your app. Whether it’s joining a meeting, sending a message, or creating a new email, I make complex tasks just one click away."
        },
        "ai_app_awareness": {
            "title": "AI App Awareness",
            "description": "I adjust my controls based on the app you’re using.",
            "longDescription": "I pay attention to what’s active on your screen so I can give you the right controls at the right time. No clutter, no guesswork—just what you need, when you need it. All on your device and without sending data to the cloud."
        },
        "ai_rewrite": {
            "title": "AI Rewrite",
            "description": "I can rewrite your text to improve tone, update formatting, or expand on details.",
            "longDescription": "I edit text right where you’re working. I can make it friendlier, more formal, shorter, longer, or more detailed — whatever you need to get your point across better."
        },
        "ai_translation": {
            "title": "AI Translation",
            "description": "I can translate between languages or even programming languages.",
            "longDescription": "I let you turn text into another spoken language or convert code into another programming language. I work in place, so you don’t have to leave the app you’re using."
        },
        "head_mouse": {
            "title": "Head Mouse",
            "description": "I let you move your mouse cursor with head movements.",
            "longDescription": "I smoothly translate your head motion into cursor motion. With features like glide and sensitivity tuning, I give you precise, comfortable, hands-free control."
        },
        "head_joystick": {
            "title": "Head Joystick",
            "description": "I let you use your head as a joystick for games or apps.",
            "longDescription": "I map your head movements onto joystick axes so you can play games or control creative software without hardware. You move naturally, and I translate it into analog input."
        },
        "mcp_agent": {
            "title": "MCP Agent",
            "description": "I can connect with external AI agents through the Model Context Protocol (MCP).",
            "longDescription": "I speak the Model Context Protocol, which lets me work alongside other AI agents and tools. That means I can coordinate tasks, exchange context, and extend my abilities with specialized external systems — all while keeping you in control."
        },
        "byo_ai_integration": {
            "title": "Bring Your Own AI",
            "description": "I let you enhance my existing AI with your own AI models or services to use with me.",
            "longDescription": "If you already have an AI service, model, or provider you trust, I can integrate it into my workflow. I give you the flexibility to choose the engine that powers generation, rewriting, or translation—whether it’s cloud, local, or custom. When you are offline or if the service is unavailable, I can fall back to my built-in capabilities."
        },
        "workflows": {
            "title": "Workflows",
            "description": "I help you chain together actions into repeatable workflows.",
            "longDescription": "I let you design and run multi-step processes that connect different inputs, commands, and outputs. With workflows, you can automate routine tasks or create complex interactions—triggered by your voice, or gestures — so I do the heavy lifting for you."
        },
        "showMore": "Show More",
        "showLess": "Show Less"
    },

    "listing": {
        "title": "Use Cases",
        "titleLong": "Explore How I Can Help You",
        "recommended": "Recommended For You",
        "exploreMore": "Explore More Use Cases",
        "description": "Discover everything I can do to enhance your workflow. From productivity boosts to creative assistance, find the perfect use cases tailored to your needs.",
        "all": "All",
        "search": "Search use cases, apps, or capabilities...",
        "noResults": "No results found. Try adjusting your search or filters.",
        "emptyAction": "View More Online"
    },
    "examples": {
        "universalAppControl": {
            "title": "Universal App Control",
            "description": "Control any application on your computer with voice commands and smart automation",
            "descriptionLong": "Take control of any application with powerful voice commands and intelligent automation. Our Universal App Control system learns how you work and adapts to your needs, making complex tasks simple and routine actions instant. From managing your productivity suite to controlling media and gaming applications, this feature works across your entire digital environment."
        },
        "handsFreeComputerControl": {
            "title": "Hands-Free Computer Control",
            "description": "Complete computer control using head movements, voice, and eye tracking for accessibility",
            "descriptionLong": "Experience a new level of computer accessibility with our hands-free control system. Using a combination of advanced eye tracking, precise head movement detection, and voice commands, this feature allows anyone to fully control their computer without using their hands. Perfect for users with mobility impairments, multitaskers, or anyone who wants to use their computer in a new way."
        },
        "replyToSocialMediaPosts": {
            "title": "Faster Social Media Posts and Replies with Dictation",
            "description": "Reply to any social media post using intelligent dictation for quick responses.",
            "descriptionLong": "Cephable’s intelligent dictation makes it faster to post and comment on social media with voice-to-text. Dictation works in any text box or post field, letting you stay active on social media without it taking over your day."
        },
        "clinicalNoteTaking": {
            "title": "HIPAA-Compliant Dictation and Formatting for Clinicians",
            "description": "Save time by taking clinical notes between sessions using intelligent voice controls and dictation. Then, auto format notes in SOAP style.",
            "descriptionLong": "Looking for HIPAA-compliant dictation software? Cephable helps clinicians document patient notes quickly with intelligent voice controls and automatic formatting. Create SOAP notes, progress reports, and logs—all hands-free.",
            "upgrade": {
                "description": "Upgrade to access HIPAA-compliant, offline Intelligent Tasks for auto formatting."
            }
        },
        "vibeCoding": {
            "title": "From Thought to Code: Dictation for Developers & Vibe Coders",
            "description": "Vibe Code with dictation and AI text generation to write boilerplate code and prototype faster.",
            "descriptionLong": "Cephable Dictation helps developers move from thought to code faster. This demo shows how voice commands can generate components, dashboards, and more inside tools like Vercel V0, GitHub Copilot in VS Code, Base 44, and Lovable. Dictate your ideas, then refine with code.",
            "upgrade": {
                "description": "Upgrade to access offline Intelligent Tasks for in-app code rewrite, generation, and translations."
            }
        },
        "controllingBackgroundMusic": {
            "title": "Media Controls: Manage Music, Video, and more without Interrupting Your Flow",
            "description": "Manage your background music using voice controls and quick actions while staying focused on the present task.",
            "descriptionLong": "Cephable voice commands make it easy to play, pause, skip, and control volume in Spotify hands-free. Quick action buttons and out-of-the-box controls also work across other apps like Apple Music, YouTube, and Netflix and 200+ other apps!"
        },
        "editingVideos": {
            "title": "Video Editing with Voice",
            "description": "Improve your video editing workflow using voice controls to navigate, cut, and edit faster.",
            "descriptionLong": "Focus on the story, not the clicks. Cephable voice controls streamline video editing across the tools you already use—Clipchamp, Adobe, iMovie, DaVinci Resolve, and more. Faster edits, fewer steps."
        },
        "inboxTriage": {
            "title": "AI Email Assistant: Automate, Draft & Reply Faster",
            "description": "Manage your inbox using voice controls and On-Device AI text generation to triage, write, and edit emails.",
            "descriptionLong": "Speed up your email process with private on-device AI. Cephable combines voice commands, dictation, quick action buttons, and text generation to make managing your inbox faster and easier. Delete emails, draft replies, and generate creative responses hands-free in Outlook, Gmail, and more.",
            "upgrade": {
                "description": "Upgrade to access offline Intelligent Tasks for auto formatting, content generation, translations, and more."
            }
        },
        "summarizingReports": {
            "title": "Summarize Confidential Reports with Private, On-Device AI",
            "description": "Summarize lengthy reports to your team while staying private and on-device using AI text generation.",
            "descriptionLong": "Turn long reports into clear team updates with on-device AI. Cephable extracts key points and numbers, then generates concise summaries you can share directly with your team. All processing happens on your device, so your sensitive data stays secure."
        },
        "handsFreeMeeting": {
            "title": "Reduce Meeting Fatigue with Hands-Free Controls",
            "description": "Control your video meetings using voice commands, quick action buttons, and smart dictation.",
            "descriptionLong": "Stay focused in meetings—without clicking through menus. Cephable brings voice commands and quick action buttons to Microsoft Teams, Zoom, Google Meet, and more. Mute/unmute, toggle video, send chat messages, raise your hand, and navigate meeting windows hands-free with ready-to-use controls you can customize for your workflow."
        },
        "spreadsheetReviews": {
            "title": "Work Faster in Spreadsheets with Voice Controls & AI Automation",
            "description": "Review and edit large spreadsheets quickly using voice controls for navigation, formatting, filtering, and editing.",
            "descriptionLong": "Cephable brings voice controls and quick action buttons to Excel, Google Sheets, and other spreadsheet tools. Review, edit, comment quickly, trigger macros and shortcuts, and customize commands for your workflow. Hundreds of controls are ready to use instantly or tailored to your setup."
        },
        "socialMediaWriting": {
            "title": "Content Creation and Research with Private, On-Device AI",
            "description": "Draft and Write Social Media Posts using voice controls and AI text generation for engaging content.",
            "descriptionLong": "Create content securely with Cephable’s on-device AI—no cloud required. Instantly generate or rewrite social posts, emails, taglines, and documentation directly inside your favorite tools. Everything runs locally, so your sensitive content stays private and you can work anywhere."
        },
        "fpsGamePlay": {
            "title": "Play FPS Games with Voice Controls",
            "description": "Play and customize a first-person shooter game using voice controls for movement, aiming, and shooting.",
            "descriptionLong": "Experience a new way to play first-person shooter games with voice controls. Use your voice to move, aim, and shoot, allowing for a more immersive and hands-free gaming experience."
        },
        "customizingGameControls": {
            "title": "Customizing RPG Game Controls",
            "description": "Customize role play game controls for better accessibility and game play.",
            "descriptionLong": "Tailor your gaming experience with customizable controls that enhance accessibility and gameplay. Adjust settings to suit your preferences and play style, ensuring a comfortable and enjoyable gaming session."
        },
        "webBrowsing": {
            "title": "Hands-Free Web Browsing with Cephable | Plan Trips, Shop Online, and More",
            "description": "Browse the web hands-free using voice controls to navigate, scroll, and fill forms.",
            "descriptionLong": "Search, shop, and plan without typing. Cephable’s intelligent voice controls work in any browser—whether booking flights, shopping online, or researching your next trip."
        },
        "documentCode": {
            "title": "Fast, Secure Code Documentation with On-Device AI Text Generation",
            "description": "Document code without sending sensitive data to the cloud, while staying in the same app using Cephable's Intelligent Rewrite.",
            "descriptionLong": "Cephable’s Intelligent Rewrite creates developer documentation directly from code—without sending sensitive data to the cloud. Generate README files, Markdown docs, and API references quickly and securely, all within the app."
        },
        "generateCode": {
            "title": "Fast, Secure Code Generation with On-Device AI Text Generation",
            "description": "Prototype code snippets from ticket requirements using AI Intelligent Tasks.",
            "descriptionLong": "Cephable lets you turn ticket requirements into working code in any language. Use Intelligent Tasks to scaffold code, generate documentation, and add unit tests automatically. Work across programming languages and apps without losing context."
        },
        "editingImages": {
            "title": "Faster Editing in Creative Apps with Voice Controls and Dictation",
            "description": "Edit images and designs using voice controls and smart dictation, for faster iterations.",
            "descriptionLong": "Bring your visual ideas to life with voice controlled image editing. Navigate faster, and iterate on designs without having to click through complex menus."
        },
        "requirementDocsFromMeeting": {
            "title": "From Notes to Product Requirement Docs in Minutes using On-Device AI",
            "description": "Save hours by transforming raw meeting transcripts into structured product requirement documents in minutes.",
            "descriptionLong": "Cephable transforms raw meeting transcripts into structured product requirement documents in minutes using Intelligent Tasks. Capture product, marketing, and user needs quickly and keep confidential discussions private with on-device AI. Save results as new documents instantly."
        },
        "gamingVoiceControls": {
            "title": "Voice Control in WoW | Quick Navigation for Menus, PvP and Dungeon Finder",
            "description": "Control your gameplay using voice commands to navigate menus, initiate PvP battles, and find dungeons.",
            "descriptionLong": "Enhance your gaming experience with voice-controlled management. Use simple voice commands to navigate menus, initiate PvP battles, and find dungeons, allowing you to focus on your gameplay while maintaining full control."
        },
        "headMouseMovement": {
            "title": "Hands-Free Gaming Setup with Head Mouse",
            "description": "Control your mouse cursor using head movements for precise navigation and clicking.",
            "descriptionLong": "Head Mouse controls enable hands-free gaming. Tilt or turn your head to move the cursor, customize speed and sensitivity, and take control without a traditional mouse."
        },
        "noteTakingAndDocumentation": {
            "title": "Note Taking and Document Generation using Private, On-Device AI",
            "description": "Take notes and generate documents using voice controls and AI text generation for efficient writing.",
            "descriptionLong": "Streamline your note taking with intelligent dictation and AI-powered document generation. Quickly create drafts, outlines, and polished documents, so you can focus on ideas instead of formatting. All processing happens on-device for privacy and security.",
            "upgrade": {
                "description": "Upgrade to access offline Intelligent Tasks for formatting, content iteration, and more."
            }
        },
        "presentationControls": {
            "title": "Hands-Free Presentation Controls with Voice Commands & Quick Actions",
            "description": "Control your presentations using voice commands to navigate slides, start/stop presentations, and highlight points.",
            "descriptionLong": "Deliver presentations with confidence using voice commands. Navigate slides, start and stop presentations, and highlight key points—all without touching your computer."
        },
        "designingFliers": {
            "title": "Designing in Creative Apps",
            "description": "Create and edit designs using voice controls and smart dictation for faster iterations.",
            "descriptionLong": "Bring your creative vision to life with voice-controlled design tools. Effortlessly navigate, edit, and iterate on your designs without getting lost in complex menus—so you can focus on creativity, not clicks."
        },
        "motionDesign": {
            "title": "Voice Commands for Motion Design and Animation",
            "description": "Animate scenes and control keyframes using intuitive voice controls for seamless motion design.",
            "descriptionLong": "Bring movement to your ideas with voice controls and animation tools. Effortlessly control timelines, keyframes, and effects so you can focus on storytelling and visual rhythm instead of complex interfaces."
        }
    }
}
